/***************************************************************************************
 * FileName      : monitoringDataGenerator.ts
 * Description   : 모니터링 더미 데이터 생성 유틸리티
 *                 - 센서 타입별 더미 값 생성 (Temperature, Pressure, Flow, Level, Concentration)
 *                 - 제어 장비 더미 값 생성 (Command ON/OFF, Setpoint 수치)
 *                 - LearningPanel의 monitoring 카드 및 useEffect에서 공통 사용
 * Author        : (주)한국공학기술연구원 박은정
 * Created Date  : 2026.02.12
 * Modified Date : 2026.02.12
 * Reference     : packages/scjeil-smart_ict_lab-app/src/components/LearningPanel.tsx (기존 중복 로직 추출)
 ***************************************************************************************/

// 2026.02.12 : 박은정 : 장비 데이터 서비스에서 타입 및 상수 임포트
import {
  EquipmentDataService,
  SensorTypes,
  type SensorType,
  type ControlData,
} from '../services/EquipmentDataService';
// 2026.02.12 : 박은정 : 모니터링 데이터 타입 임포트 (순환 의존성 해소)
import { MonitoringData } from '../types/MonitoringData';

// 2026.02.12 : 박은정 : 모니터링 예시 데이터 JSON 임포트 (제어 장비 상태 + 계측 장비 센서 값)
import heatExchangerMonitoring from '../../assets/json/monitoring/heat-exchanger.json';
import distillationMonitoring from '../../assets/json/monitoring/distillation.json';
import circulationMonitoring from '../../assets/json/monitoring/circulation.json';

// 2026.02.12 : 박은정 : 장비별 예시 모니터링 데이터 맵 (equipmentId → MonitoringData)
interface MonitoringJsonData {
  equipmentId: string;
  controlValues: Record<string, string>;
  sensorValues: Record<string, string>;
}
const EXAMPLE_MONITORING_MAP: Record<string, MonitoringJsonData> = {};
[heatExchangerMonitoring, distillationMonitoring, circulationMonitoring].forEach((data) => {
  const d = data as unknown as MonitoringJsonData;
  EXAMPLE_MONITORING_MAP[d.equipmentId] = d;
});

// 2026.02.12 : 박은정 : deviceId 기반 시드 값 계산 (결정적 난수 생성용)
function computeSeed(id: string): number {
  return id.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
}

// 2026.02.12 : 박은정 : 센서 타입에 따른 더미 값 생성
export function generateDummySensorValue(sensorType: SensorType, deviceId: string): string {
  const seed = computeSeed(deviceId);
  const baseValue = (seed % 100) / 10; // 0.0 ~ 9.9 범위

  switch (sensorType) {
    case SensorTypes.Temperature:
      return (20 + (baseValue * 6)).toFixed(1);
    case SensorTypes.Pressure:
      return (0.5 + (baseValue * 0.45)).toFixed(2);
    case SensorTypes.Flow:
      return (10 + (baseValue * 9)).toFixed(1);
    case SensorTypes.Level:
      return (100 + (baseValue * 40)).toFixed(0);
    case SensorTypes.Concentration:
      return (20 + (baseValue * 6)).toFixed(1);
    default:
      return '-';
  }
}

// 2026.02.12 : 박은정 : 제어 장비 더미 값 생성
export function generateDummyControlValue(control: ControlData): string {
  if (control.dataType === 'bool') {
    const seed = computeSeed(control.controlId);
    return (seed % 3 === 0) ? 'ON' : 'OFF';
  }

  if (control.dataType === 'float' || control.dataType === 'int') {
    const seed = computeSeed(control.controlId);
    const baseValue = (seed % 100) / 10;
    if (control.controlType === 'Setpoint') {
      if (control.unit === 'Hz') {
        return (baseValue * 6).toFixed(1);
      } else if (control.unit === '°C') {
        return (20 + baseValue * 5).toFixed(1);
      } else if (control.unit === '%') {
        return (baseValue * 10).toFixed(0);
      } else {
        return baseValue.toFixed(1);
      }
    }
  }

  return '-';
}

// 2026.04.24 : 박은정 : 알고리즘 기반 더미 값 생성 (JSON 예시 데이터 미사용)
//   - 초기 진입 시 모니터 텍스트 라벨에 표시할 "데이터 모니터링 카드와 다른 더미 값" 생성에 재사용
function buildAlgorithmicDummyData(equipmentId: string): MonitoringData {
  const equipmentDataService = EquipmentDataService.getInstance();
  const monitoringSections = equipmentDataService.getMonitoringData(equipmentId);
  const allDevices = equipmentDataService.getDevices(equipmentId);

  // 2026.02.12 : 박은정 : 센서 ID(sensorId) → 센서 타입 매핑 생성
  const sensorMap: Record<string, { sensorType: SensorType; unit?: string }> = {};
  for (const device of allDevices) {
    if (device.sensors && device.sensors.length > 0) {
      for (const sensor of device.sensors) {
        sensorMap[sensor.sensorId] = {
          sensorType: sensor.sensorType,
          unit: sensor.unit,
        };
      }
    }
  }

  const sensorValues: Record<string, string> = {};
  for (const section of monitoringSections) {
    for (const row of section.dataRows) {
      const sensorInfo = sensorMap[row.label];
      if (sensorInfo) {
        sensorValues[row.label] = generateDummySensorValue(sensorInfo.sensorType, row.label);
      }
    }
  }

  const controlDevices = equipmentDataService.getControlDevices(equipmentId);
  const controlValues: Record<string, string> = {};
  for (const device of controlDevices) {
    if (device.controls && device.controls.length > 0) {
      for (const control of device.controls) {
        controlValues[control.controlId] = generateDummyControlValue(control);
      }
    }
  }

  return { sensorValues, controlValues };
}

// 2026.02.12 : 박은정 : 장비 ID 기반 전체 모니터링 데이터 생성 (센서 값 + 제어 값)
// → JSON 예시 데이터가 있으면 우선 사용, 없으면 알고리즘 더미 값 생성
export function buildMonitoringData(equipmentId: string): MonitoringData {
  // 2026.02.12 : 박은정 : JSON 예시 데이터에서 모니터링 값 로드 (우선 사용)
  const exampleData = EXAMPLE_MONITORING_MAP[equipmentId];
  if (exampleData) {
    return {
      sensorValues: { ...exampleData.sensorValues },
      controlValues: { ...exampleData.controlValues },
    };
  }

  // 2026.02.12 : 박은정 : JSON 예시 데이터가 없으면 알고리즘 기반 더미 값 생성 (fallback)
  return buildAlgorithmicDummyData(equipmentId);
}

// 2026.04.24 : 박은정 : 서비스 초기 진입 시 모니터 텍스트 라벨용 더미 값 생성
//   - 데이터 모니터링/시뮬레이션 카드가 사용하는 JSON 예시 값(buildMonitoringData) 과
//     다른 값을 보여주기 위해 항상 알고리즘 기반 생성 사용
//   - monitoring/scenario 카드 진입 시 실측 값으로 덮어써짐
export function buildInitialDummyData(equipmentId: string): MonitoringData {
  return buildAlgorithmicDummyData(equipmentId);
}

